/**
 * Que una etiqueta larga se siga pudiendo leer en un desplegable estrecho
 * -----------------------------------------------------------------------------
 * MDB pinta lo elegido en un `<input>` de solo lectura del ancho de su columna.
 * Cuando el texto no cabe, el navegador enseña **el principio** y esconde el
 * final. Con etiquetas normales da igual; con una ruta no:
 *
 *   FILETES › SIN ADOBO › FILETE DE 100G › KILOGRAMO
 *   └───────────── se ve ─────────────┘ └── se pierde ──┘
 *
 * Lo que se pierde es la hoja, que es justo lo que dice **cuál** de las tres
 * que se llaman igual se eligió. Queda a la vista la parte que comparten todas.
 *
 * Aquí se hacen dos cosas y las dos hacen falta:
 *
 * 1. El campo se desplaza al final, así que se lee «…FILETE DE 100G ›
 *    KILOGRAMO». Se pierde el principio, que es lo que se puede deducir.
 * 2. El texto entero va en `title`, para verlo al pasar por encima sin tener
 *    que abrir el desplegable.
 *
 * Se aplica a **todos** los desplegables, no solo a los de categorías: el
 * problema es del ancho, no de qué se está eligiendo.
 */

/**
 * Pone al día un campo de desplegable: título completo y final a la vista.
 *
 * @param {HTMLInputElement} campo
 */
/**
 * La segunda línea de lo elegido -de quién cuelga-, o cadena vacía.
 *
 * En la lista se ve debajo del nombre, pero el campo cerrado solo tiene sitio
 * para una línea y enseña el nombre. Con nombres que se repiten -una categoría
 * «100 G» que cuelga de dos sitios- eso deja de decir cuál es.
 *
 * @param {HTMLInputElement} campo
 * @returns {string}
 */
function segundaLineaDeLoElegido(campo) {
  const envoltorio = campo.closest('.select-wrapper');

  if (!envoltorio || !envoltorio.id) return '';

  const original = document.getElementById(envoltorio.id.replace(/^select-wrapper-/, ''));

  if (!original) return '';

  const elegidas = Array.prototype.filter.call(
    original.options,
    (opcion) => opcion.selected && opcion.value !== ''
  );

  return elegidas
    .map((opcion) => opcion.getAttribute('data-mdb-secondary-text') || '')
    .filter(Boolean)
    .join(' · ');
}

/**
 * Pone al día un campo de desplegable: título completo y final a la vista.
 *
 * @param {HTMLInputElement} campo
 */
function repasar(campo) {
  const texto = String(campo.value || '');

  if (texto === '') {
    campo.removeAttribute('title');

    return;
  }

  const debajo = segundaLineaDeLoElegido(campo);
  const cabe = campo.scrollWidth <= campo.clientWidth + 1;

  // Lo elegido lleva una segunda línea que aquí no se ve: va al globo aunque el
  // nombre quepa entero. Es lo que dice **cuál** de las que se llaman igual es,
  // y sin esto se pierde en cuanto se cierra el desplegable.
  if (debajo !== '') {
    campo.setAttribute('title', texto + ' -' + debajo);
    campo.scrollLeft = cabe ? 0 : campo.scrollWidth;

    return;
  }

  // Sin segunda línea, el globo solo si de verdad no cabe: uno que repite lo que
  // ya se lee entero aparece sin aportar nada.
  if (!cabe) {
    campo.setAttribute('title', texto);
    campo.scrollLeft = campo.scrollWidth;

    return;
  }

  campo.removeAttribute('title');
  campo.scrollLeft = 0;
}

/**
 * Vigila los desplegables que haya dentro de `raiz`.
 *
 * @param {ParentNode} [raiz=document]
 */
/** El vigilante de la página se pone una sola vez. */
let vigilando = false;

/** Repasa todos los que haya montados, después de que el DOM se asiente. */
function repasarTodos() {
  setTimeout(() => {
    document.querySelectorAll('[data-company-etiqueta-larga]').forEach(repasar);
  }, 0);
}

/**
 * Vigila los desplegables que haya dentro de `raiz`.
 *
 * ## Por qué se escucha a la página y no al desplegable
 *
 * Lo natural sería enterarse por el `<select>` de debajo, pero **MDB no lanza
 * nada** al elegir: ni `change` en el select, ni `input` en el campo, ni sus
 * propios eventos. Se comprobó escuchando los cuatro; no llegó ninguno. Y el
 * valor lo escribe a mano, así que tampoco hay atributo que observar.
 *
 * Así que se escucha el clic y el teclado de la página, y se repasa después.
 * Depender de un evento suyo sería atarse a la versión de MDB; esto sigue
 * funcionando cuando la actualicen.
 *
 * @param {ParentNode} [raiz=document]
 */
export function cuidarEtiquetasLargas(raiz = document) {
  if (!raiz || typeof raiz.querySelectorAll !== 'function') return;

  if (!vigilando) {
    vigilando = true;
    // En captura: la lista vive en un trozo aparte colgado del `body`, fuera
    // del formulario, y algunos manejadores de MDB cortan la burbuja.
    document.addEventListener('click', repasarTodos, true);
    document.addEventListener('keyup', repasarTodos, true);
  }

  raiz.querySelectorAll('.select-wrapper .select-input').forEach((campo) => {
    if (campo.dataset.companyEtiquetaLarga) return;

    campo.dataset.companyEtiquetaLarga = '1';

    // Al pasar por encima también: el ancho de la columna cambia al girar el
    // móvil o al abrir un panel, y entonces lo que cabía deja de caber.
    campo.addEventListener('mouseenter', () => repasar(campo));

    repasar(campo);
  });
}

export default cuidarEtiquetasLargas;
